import type { FC, ReactNode } from 'react';

// Externals
import PropTypes from 'prop-types';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

// Store
import { useSelector } from '../store';
import { Shop } from 'src/types/shop.model';

type RootGuardProps = {
  children: ReactNode;
};

export const RootGuard: FC<RootGuardProps> = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isAuthenticated = useSelector<boolean>((state) => state.authentication.isAuthenticated);
  const shops = useSelector<Shop[]>((state) => state.authentication.shops);
  const isAdmin = useSelector<boolean>((state) => state.authentication.isAdmin);

  if (!isAuthenticated) {
    return <Navigate replace={true} to={`/login?redirect=${encodeURIComponent(location.pathname)}`} />;
  }

  if (isAdmin) {
    return <Navigate replace={true} to={'/admin'} />;
  }

  if (shops.length > 0) {
    return <Navigate replace={true} to={`/${shops[0].id}`} />;
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (searchParams.get('redirect') && location.pathname !== searchParams.get('redirect')) {
    return <Navigate replace={true} to={searchParams.get('redirect')} />;
  }

  return null;
};

RootGuard.propTypes = {
  children: PropTypes.node
};
